import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { getTagColor, sortTagsByColorOrder } from '../utils/tagColors';

const FilterContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 2rem auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FilterBox = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.filterBorder};
  overflow: hidden;
`;

const CollapsibleSection = styled.div`
  border-top: 1px solid ${props => props.theme.colors.filterBorder};
`;

const CollapsibleHeader = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.borderLight};
  }
`;

const CollapsibleContent = styled(motion.div)`
  padding: 0 1.5rem 1.5rem;
`;

const AlwaysVisibleContent = styled.div`
  padding: 1.5rem;
`;

const SectionLabel = styled.h3`
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const FilterIcon = styled(motion.div)`
  font-size: 1rem;
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActiveFiltersCount = styled.span`
  background: rgba(100, 255, 218, 0.2);
  color: #64ffda;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const FilterBubble = styled.button`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  border: 1px solid ${props => props.active ? '#64ffda' : props.tagColor || props.theme.colors.border};
  background: ${props => props.active 
    ? 'linear-gradient(135deg, rgba(100, 255, 218, 0.2) 0%, rgba(187, 134, 252, 0.2) 100%)'
    : props.tagColor || props.theme.colors.surfaceSecondary
  };
  color: ${props => props.active ? '#64ffda' : props.theme.colors.text};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, rgba(100, 255, 218, 0.3) 0%, rgba(187, 134, 252, 0.3) 100%)'
      : props.tagColor ? `linear-gradient(135deg, ${props.tagColor} 0%, ${props.theme.colors.borderLight} 100%)` : props.theme.colors.borderLight
    };
    border-color: ${props => props.active ? '#64ffda' : props.theme.colors.border};
  }
`;

const ClearAllButton = styled.button`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 100, 100, 0.5);
  background: rgba(255, 100, 100, 0.1);
  color: #ff6464;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 100, 100, 0.2);
    border-color: rgba(255, 100, 100, 0.8);
  }
`;

const YearRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
`;

const RangeSliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  flex: 1;
`;

const RangeTrack = styled.div`
  position: absolute;
  width: 100%;
  height: 4px;
  background: ${props => props.theme.colors.borderLight};
  border-radius: 2px;
  top: 50%;
  transform: translateY(-50%);
`;

const RangeTrackActive = styled.div`
  position: absolute;
  height: 4px;
  background: linear-gradient(90deg, #64ffda 0%, #bb86fc 100%);
  border-radius: 2px;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.left}%;
  width: ${props => props.width}%;
`;

const RangeDot = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background: #64ffda;
  border: 2px solid ${props => props.theme.colors.background};
  border-radius: 50%;
  cursor: pointer;
  top: 50%;
  transform: translate(-50%, -50%);
  left: ${props => props.left}%;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px ${props => props.theme.colors.shadow};
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 4px 12px rgba(100, 255, 218, 0.4);
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }
`;

const YearMarkersContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 0px;
  height: 16px;
`;

const YearMarker = styled.div`
  position: absolute;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.65rem;
  font-weight: 500;
  opacity: 0.8;
  left: ${props => props.position}%;
  transform: translateX(-50%);
  white-space: nowrap;
`;



const RangeSlider = ({ min, max, value, onChange }) => {
  const { themeData } = useTheme();
  const [isDragging, setIsDragging] = useState(null); // 'min' or 'max'
  const sliderRef = useRef(null);

  const getPercentage = useCallback((val) => {
    if (max === min) return 0; // Prevent division by zero
    const percentage = ((val - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, percentage)); // Clamp between 0-100
  }, [min, max]);
  
  const handleMouseDown = (type) => (e) => {
    e.preventDefault();
    setIsDragging(type);
  };

  const handleTouchStart = (type) => (e) => {
    e.preventDefault();
    setIsDragging(type);
  };

  // Round year to nearest 5-year increment (2005, 2010, 2015, etc.)
  const roundToFiveYears = useCallback((year) => {
    return Math.round(year / 5) * 5;
  }, []);

  const getValueFromPercentage = useCallback((percentage) => {
    if (max === min) return min; // Prevent NaN
    const val = min + (percentage / 100) * (max - min);
    const roundedVal = roundToFiveYears(val);
    return Math.max(min, Math.min(max, roundedVal));
  }, [min, max, roundToFiveYears]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    
    if (!clientX || rect.width === 0) return; // Prevent invalid calculations
    
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const newValue = getValueFromPercentage(percentage);

    if (isDragging === 'min') {
      const newMin = Math.min(newValue, value.max);
      if (newMin !== value.min) {
        onChange({ min: newMin, max: value.max });
      }
    } else if (isDragging === 'max') {
      const newMax = Math.max(newValue, value.min);
      if (newMax !== value.max) {
        onChange({ min: value.min, max: newMax });
      }
    }
  }, [isDragging, value, onChange, getValueFromPercentage]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  const handleTrackClick = useCallback((e) => {
    if (isDragging || !sliderRef.current) return; // Don't interfere with dragging
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clientX = e.clientX;
    
    if (!clientX || rect.width === 0) return;
    
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const newValue = getValueFromPercentage(percentage);
    
    // Calculate distances to both handles to determine which one to move
    const minPercentage = getPercentage(value.min);
    const maxPercentage = getPercentage(value.max);
    
    const distanceToMin = Math.abs(percentage - minPercentage);
    const distanceToMax = Math.abs(percentage - maxPercentage);
    
    // Move the closest handle (if equal distance, prefer left handle)
    if (distanceToMin <= distanceToMax) {
      // Move left handle
      const newMin = Math.min(newValue, value.max);
      if (newMin !== value.min) {
        onChange({ min: newMin, max: value.max });
      }
    } else {
      // Move right handle
      const newMax = Math.max(newValue, value.min);
      if (newMax !== value.max) {
        onChange({ min: value.min, max: newMax });
      }
    }
  }, [isDragging, value, onChange, getValueFromPercentage, getPercentage]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('touchend', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const minPercentage = getPercentage(value.min);
  const maxPercentage = getPercentage(value.max);

  // Generate year markers for display - show clean decade markers
  const yearMarkers = [];
  
  // Use 10-year steps for clean decade display
  const step = 10;
  
  // Find the first marker year (round down to nearest decade)
  const firstMarker = Math.floor(min / step) * step;
  
  // Generate markers at decade intervals
  for (let year = firstMarker; year <= max; year += step) {
    if (year >= min) {
      yearMarkers.push(year);
    }
  }
  
  // Always include the max year if it's not already included
  if (!yearMarkers.includes(max) && max !== min) {
    yearMarkers.push(max);
  }

  return (
    <div style={{ width: '100%' }}>
      <RangeSliderContainer ref={sliderRef} onClick={handleTrackClick}>
        <RangeTrack theme={themeData} />
        <RangeTrackActive 
          left={minPercentage} 
          width={maxPercentage - minPercentage} 
        />
        <RangeDot 
          left={minPercentage}
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleTouchStart('min')}
          onClick={(e) => e.stopPropagation()} // Prevent track click when dragging dots
          theme={themeData}
        />
        <RangeDot 
          left={maxPercentage}
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleTouchStart('max')}
          onClick={(e) => e.stopPropagation()} // Prevent track click when dragging dots
          theme={themeData}
        />
      </RangeSliderContainer>
      
      <YearMarkersContainer>
        {yearMarkers.map(year => {
          const position = ((year - min) / (max - min)) * 100;
          return (
            <YearMarker key={year} theme={themeData} position={position}>
              {year}
            </YearMarker>
          );
        })}
      </YearMarkersContainer>
    </div>
  );
};

const FilterSection = ({ tags, authors, activeFilters, activeAuthors, onFilterChange, onAuthorFilterChange, yearRange, availableYearRange, onYearRangeChange }) => {
  const { themeData } = useTheme();
  const [isAuthorsExpanded, setIsAuthorsExpanded] = useState(false);
  
  // Check if year range is actually filtered (not at default/full range)
  const isYearRangeFiltered = yearRange && availableYearRange && 
    yearRange.min !== null && yearRange.max !== null &&
    (yearRange.min !== availableYearRange.min || yearRange.max !== availableYearRange.max);
  
  // Check if tags are actually filtered (not just "All" selected)
  const isTagsFiltered = activeFilters.length > 0 && 
    (activeFilters.length > 1 || !activeFilters.includes('All'));
  
  // Check if authors are actually filtered (not just "All" selected)
  const isAuthorsFiltered = activeAuthors.length > 0 && 
    (activeAuthors.length > 1 || !activeAuthors.includes('All'));
  
  // Count of active filters for display
  const activeAuthorsCount = activeAuthors.filter(author => author !== 'All').length;
  
  // Only show clear button if there are actual filters applied
  const hasActiveFilters = isTagsFiltered || isAuthorsFiltered || isYearRangeFiltered;
  
  const handleTagClick = (tag) => {
    if (tag === 'All') {
      onFilterChange(['All']);
    } else {
      const newFilters = activeFilters.includes(tag)
        ? activeFilters.filter(f => f !== tag)
        : [...activeFilters.filter(f => f !== 'All'), tag];
      
      onFilterChange(newFilters.length === 0 ? ['All'] : newFilters);
    }
  };

  const handleAuthorClick = (author) => {
    if (author === 'All') {
      onAuthorFilterChange(['All']);
    } else {
      const newAuthors = activeAuthors.includes(author)
        ? activeAuthors.filter(a => a !== author)
        : [...activeAuthors.filter(a => a !== 'All'), author];
      
      onAuthorFilterChange(newAuthors.length === 0 ? ['All'] : newAuthors);
    }
  };

  const handleYearRangeChange = (min, max) => {
    // Validate against available range
    const validMin = min && availableYearRange ? Math.max(min, availableYearRange.min) : min;
    const validMax = max && availableYearRange ? Math.min(max, availableYearRange.max) : max;
    
    onYearRangeChange({ min: validMin, max: validMax });
  };
  
  const handleClearAll = () => {
    onFilterChange(['All']);
    onAuthorFilterChange(['All']);
    // Reset to full year range
    if (onYearRangeChange) {
      onYearRangeChange({ min: null, max: null });
    }
  };
  
  return (
    <FilterContainer>
      <FilterBox theme={themeData}>
        <AlwaysVisibleContent>
          {/* Year Range - Always Visible */}
          {yearRange && availableYearRange && (
            <div style={{ marginBottom: '2rem' }}>
              <SectionLabel theme={themeData}>Year Range</SectionLabel>
              <YearRangeContainer>
                <RangeSlider
                  min={availableYearRange.min}
                  max={availableYearRange.max}
                  value={{ min: yearRange.min, max: yearRange.max }}
                  onChange={({ min, max }) => handleYearRangeChange(min, max)}
                />
              </YearRangeContainer>
            </div>
          )}

          {/* Tags - Always Visible */}
          <div>
            <SectionLabel theme={themeData}>Categories</SectionLabel>
            <FilterRow>
              {sortTagsByColorOrder([...tags]).map(tag => (
                <FilterBubble
                  key={tag}
                  active={activeFilters.includes(tag)}
                  onClick={() => handleTagClick(tag)}
                  tagColor={getTagColor(tag, themeData.colors.bubbleAlpha)}
                  theme={themeData}
                >
                  {tag}
                </FilterBubble>
              ))}
            </FilterRow>
          </div>
        </AlwaysVisibleContent>

        {/* Authors - Collapsible */}
        <CollapsibleSection theme={themeData}>
          <CollapsibleHeader 
            theme={themeData} 
            onClick={() => setIsAuthorsExpanded(!isAuthorsExpanded)}
          >
            <FilterIcon
              theme={themeData}
              animate={{ rotate: isAuthorsExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              ▼
            </FilterIcon>
            <span>Authors</span>
            {activeAuthorsCount > 0 && (
              <ActiveFiltersCount>{activeAuthorsCount} selected</ActiveFiltersCount>
            )}
          </CollapsibleHeader>
          
          <AnimatePresence>
            {isAuthorsExpanded && (
              <CollapsibleContent
                theme={themeData}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <FilterRow>
                  {authors.map(author => (
                    <FilterBubble
                      key={author}
                      active={activeAuthors.includes(author)}
                      onClick={() => handleAuthorClick(author)}
                      theme={themeData}
                    >
                      {author}
                    </FilterBubble>
                  ))}
                </FilterRow>
              </CollapsibleContent>
            )}
          </AnimatePresence>
        </CollapsibleSection>

        {/* Clear All Button - Only show when filters are actually applied */}
        {hasActiveFilters && (
          <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
            <ClearAllButton onClick={handleClearAll}>
              Clear All Filters
            </ClearAllButton>
          </div>
        )}
      </FilterBox>
    </FilterContainer>
  );
};

FilterSection.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  authors: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeAuthors: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onAuthorFilterChange: PropTypes.func.isRequired,
  yearRange: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number
  }),
  availableYearRange: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number
  }),
  onYearRangeChange: PropTypes.func.isRequired
};

export default FilterSection; 